import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export interface MessageInfo {
  message: string;
  code?: string;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class MessageHandlingService {
  private messageSubject = new BehaviorSubject<MessageInfo | null>(null);
  public message$ = this.messageSubject.asObservable();
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  // Handle success messages
  handleSuccess(response: HttpResponse<any>, requestMethod: string, showToast = true): void {
    if (showToast && requestMethod !== 'GET' && response.body?.message) {
      const slug = response.body.message;
      const key = `TOASTR.${slug}`;
      const translated = this.translate.instant(key);
      const message = translated !== key ? translated : slug;
      this.toastr.success(message);
    }
  }

  // Handle error messages
  handleError(error: HttpErrorResponse, showToast = true): MessageInfo {
    let errorMessage: MessageInfo;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = {
        message: error.error.message || 'An error occurred',
        code: 'CLIENT_ERROR',
        timestamp: new Date()
      };
    } else {
      // Server-side error - always use API message if available
      errorMessage = {
        message: error.error?.message || 'An error occurred',
        code: `HTTP_${error.status}`,
        timestamp: new Date()
      };
    }

    // Show error toast
    if (showToast) {
      const slug = errorMessage.message;
      const key = `TOASTR.${slug}`;
      const translated = this.translate.instant(key);
      const displayMessage = translated !== key ? translated : slug;
      this.showErrorToast(displayMessage, error.status);
    }

    this.messageSubject.next(errorMessage);
    return errorMessage;
  }

  private showErrorToast(message: string, status: number): void {
    if (status === 422) {
      if (message && typeof message === 'string') {
        this.toastr.error(message);
      }
    } else if (status === 500) {
      this.toastr.error(this.translate.instant('TOASTR.SERVER_ERROR'));
    } else if (message) {
      this.toastr.error(message);
    }
  }

  clearMessage(): void {
    this.messageSubject.next(null);
  }
}
